import React, { useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Box, Button, Text } from '@rover/kibble/core';
import { LINE_HEIGHT } from '@rover/kibble/official-patterns/OverflowText/OverflowText.common';
import useTheme from '@rover/kibble/themes/useTheme';
import {
  MaximalCardEventTags,
  SearchResultReviewsLinkClickEvent,
} from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { CommonSearchResult } from '@rover/types/src/CommonSearchResult';
import { PetType } from '@rover/types/src/Pet';

import ReviewsModal from './ReviewsModal';
import { REVIEW_LINK_FADE, SEARCH_RESULT_CARD_Z_INDEX } from './SearchResultCard.constants';

const MAX_LINE_COUNT_V1 = 3;
const MAX_LINE_COUNT_V2 = 2;

type Props = {
  searchResult: CommonSearchResult;
  preferCatReviews: boolean;
  analyticsProps: MaximalCardEventTags;
  isV2?: boolean;
};

const MaximalReviewSnippet = ({
  searchResult,
  preferCatReviews,
  analyticsProps,
  isV2,
}: Props): React.ReactElement | null => {
  const { t } = useLingui();
  // V2 condenses the card: fewer lines and smaller review text.
  const maxLineCount = isV2 ? MAX_LINE_COUNT_V2 : MAX_LINE_COUNT_V1;
  const {
    avatarUrl,
    catReviewText,
    opk,
    reviewOpk,
    reviewText,
    shortName: sitterName,
  } = searchResult;
  const reviewPetType = preferCatReviews && catReviewText ? PetType.CAT : PetType.DOG;
  const reviewTextToUse = reviewPetType === PetType.CAT ? catReviewText : reviewText;

  const theme = useTheme();
  const maxHeight = maxLineCount * LINE_HEIGHT;

  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [seeMoreButtonClick, setSeeMoreButtonClick] = useState<boolean>(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  if (!reviewTextToUse) {
    return null;
  }

  const handlePress = ({ webEvent }: { webEvent?: React.MouseEvent<HTMLElement> }): void => {
    webEvent?.preventDefault();
    webEvent?.stopPropagation();
    emitAnalyticsEvent(new SearchResultReviewsLinkClickEvent(analyticsProps));
    setShowReviewsModal(true);
  };

  const handleCloseModal = (): void => {
    setShowReviewsModal(false);
    const button = triggerRef.current?.querySelector('button');
    button?.focus();
  };

  return (
    <Box
      position="relative"
      maxHeight={`${maxHeight}px`}
      borderLeftWidth="3px"
      borderLeftStyle="solid"
      borderLeftColor={theme.colors.border.secondary}
      pl="3x"
      mt="2x"
      sx={{
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitLineClamp: maxLineCount,
          WebkitBoxOrient: 'vertical',
        }}
      >
        <Text size={isV2 ? '100' : '200'} textColor="secondary">
          &ldquo;{reviewTextToUse}&rdquo;
        </Text>
      </Box>
      <Box
        ref={triggerRef}
        position="absolute"
        sx={{
          background: REVIEW_LINK_FADE,
          bottom: '2px',
          right: 0,
          paddingLeft: '75px',
          zIndex: SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE,
        }}
      >
        <Button
          variant="link"
          size="small"
          data-testid="see-all-review-button"
          aria-label={t`See all reviews for ${sitterName}`}
          onPress={handlePress}
          sx={{ textDecoration: 'unset' }}
        >
          {t`See all`}
        </Button>
      </Box>
      <ReviewsModal
        avatarUrl={avatarUrl}
        opk={opk}
        highlightedReviewOpk={reviewOpk}
        modalOpen={showReviewsModal}
        closeModal={handleCloseModal}
        title={t`${sitterName}'s Reviews`}
        setSeeMoreButtonClick={setSeeMoreButtonClick}
        seeMoreButtonClick={seeMoreButtonClick}
      />
    </Box>
  );
};

export default MaximalReviewSnippet;
